import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  Box,
  Typography,
  IconButton,
  Chip,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import {
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  CheckCircle as CheckIcon,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import api from '../services/api';

interface PWAAccessModalProps {
  open: boolean;
  onClose: () => void;
  clientId: number | null;
  clientName: string;
  hasAccess: boolean;
  onSuccess: () => void;
}

interface PWACredentials {
  username: string;
  temp_password: string | null;
  password_changed: boolean;
  is_active: boolean;
  last_login: string | null;
  pwa_url: string;
}

const PWAAccessModal: React.FC<PWAAccessModalProps> = ({
  open,
  onClose,
  clientId,
  clientName,
  hasAccess,
  onSuccess
}) => {
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [credentials, setCredentials] = useState<PWACredentials | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  // Réinitialiser l'état à la fermeture
  const handleClose = () => {
    setUsername('');
    setError('');
    setCredentials(null);
    setShowPassword(false);
    onClose();
  };

  // Activer l'accès PWA
  const handleActivate = async () => {
    if (!username.trim()) {
      setError('Le nom d\'utilisateur est requis');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post(`/clients/${clientId}/toggle-pwa-access`, {
        enable: true,
        username: username.trim()
      });

      if (response.data.success) {
        setCredentials({
          username: response.data.data.username,
          temp_password: response.data.data.temp_password,
          password_changed: response.data.data.password_changed,
          is_active: true,
          last_login: null,
          pwa_url: window.location.origin + '/client/login'
        });
        onSuccess();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de l\'activation');
    } finally {
      setLoading(false);
    }
  };

  // Récupérer les identifiants existants
  const handleViewCredentials = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await api.get(`/clients/${clientId}/pwa-credentials`);

      if (response.data.success) {
        setCredentials(response.data.data);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de la récupération');
    } finally {
      setLoading(false);
    }
  };

  // Désactiver l'accès PWA
  const handleDeactivate = async () => {
    if (!window.confirm('Voulez-vous vraiment désactiver l\'accès PWA pour ce client ?')) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post(`/clients/${clientId}/toggle-pwa-access`, {
        enable: false
      });

      if (response.data.success) {
        onSuccess();
        handleClose();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de la désactivation');
    } finally {
      setLoading(false);
    }
  };

  // Copier dans le presse-papier
  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  // Charger les credentials à l'ouverture si le client a déjà l'accès
  React.useEffect(() => {
    if (open && hasAccess && clientId) {
      handleViewCredentials();
    }
  }, [open, hasAccess, clientId]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {hasAccess ? 'Identifiants PWA' : 'Activer l\'accès PWA'}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography variant="subtitle2" color="text.secondary">
          {clientName}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress />
          </Box>
        ) : credentials ? (
          // Afficher les identifiants
          <Box>
            <Alert severity="success" sx={{ mb: 3 }}>
              <CheckIcon sx={{ mr: 1 }} />
              Accès PWA actif
            </Alert>

            <Box mb={2}>
              <Typography variant="caption" color="text.secondary">
                Nom d'utilisateur
              </Typography>
              <TextField
                fullWidth
                value={credentials.username}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => handleCopy(credentials.username, 'username')}
                        edge="end"
                      >
                        {copiedField === 'username' ? <CheckIcon color="success" /> : <CopyIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {credentials.temp_password && !credentials.password_changed ? (
              <Box mb={2}>
                <Typography variant="caption" color="text.secondary">
                  Mot de passe temporaire
                </Typography>
                <TextField
                  fullWidth
                  type={showPassword ? 'text' : 'password'}
                  value={credentials.temp_password}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                        <IconButton
                          onClick={() => handleCopy(credentials.temp_password!, 'password')}
                          edge="end"
                        >
                          {copiedField === 'password' ? <CheckIcon color="success" /> : <CopyIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Le client doit changer ce mot de passe à sa première connexion
                </Alert>
              </Box>
            ) : (
              <Box mb={2}>
                <Alert severity="info">
                  <CheckIcon sx={{ mr: 1 }} />
                  Mot de passe changé par le client
                </Alert>
              </Box>
            )}

            <Box mb={2}>
              <Typography variant="caption" color="text.secondary">
                URL de connexion
              </Typography>
              <TextField
                fullWidth
                value={credentials.pwa_url}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => handleCopy(credentials.pwa_url, 'url')}
                        edge="end"
                      >
                        {copiedField === 'url' ? <CheckIcon color="success" /> : <CopyIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {credentials.last_login && (
              <Typography variant="caption" color="text.secondary">
                Dernière connexion : {new Date(credentials.last_login).toLocaleString('fr-FR')}
              </Typography>
            )}
          </Box>
        ) : (
          // Formulaire d'activation
          <Box>
            <Alert severity="info" sx={{ mb: 3 }}>
              Activez l'accès PWA pour permettre au client de se connecter à son espace personnel
            </Alert>

            <TextField
              fullWidth
              label="Nom d'utilisateur"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Ex: jeandupont"
              helperText="Ce nom sera utilisé pour se connecter à la PWA"
              autoFocus
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {credentials ? (
          <>
            <Button onClick={handleDeactivate} color="error" disabled={loading}>
              Désactiver PWA
            </Button>
            <Button onClick={handleClose} variant="contained">
              Fermer
            </Button>
          </>
        ) : (
          <>
            <Button onClick={handleClose} disabled={loading}>
              Annuler
            </Button>
            <Button
              onClick={handleActivate}
              variant="contained"
              disabled={loading || !username.trim()}
            >
              Activer
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PWAAccessModal;
